import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PropsWithChildren } from 'react';
import clsx from 'clsx';
import { Icon } from './components/icon';
import { Size } from '@/utils/typography';
import { removeTrailingSlash } from '@/utils/strings';
import { useBrandedColors } from '../../wp-blocks/branded-consumer-loan/hooks/use-branded-colors';

export type Variant = 'standard' | 'arrow' | 'bankidwhite' | 'bankidnorwaywhite' | 'bankidnorwaypurple';

export type ButtonType =
    | 'primary'
    | 'secondary'
    | 'secondaryBorder'
    | 'tertiary'
    | 'link'
    | 'tertiaryNew'
    | 'pink'
    | 'transparent'
    | 'sand'
    | 'custom';

const adjustHex = (hex: string, factor: number): string => {
    hex = hex.replace(/^#/, '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    if (factor < 1) {
        r = Math.round(r * factor);
        g = Math.round(g * factor);
        b = Math.round(b * factor);
    }

    if (factor >= 1) {
        r = Math.round(r + (255 - r) * (factor - 1));
        g = Math.round(g + (255 - g) * (factor - 1));
        b = Math.round(b + (255 - b) * (factor - 1));
    }

    return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
};

export type ButtonProps = {
    to?: string | null;
    target?: string;
    type?: ButtonType;
    className?: string;
    tabIndex?: number;
    size?: Size | 'tiny';
    onClick?: () => void;
    variant?: Variant;
    fullWidth?: boolean;
    disabled?: boolean;
    relativePath?: boolean;
    isSubmit?: boolean;
    ariaDisabled?: boolean;
    brandColor?: string;
    customBackgroundColor?: string;
    customTextColor?: string;
    customBorderColor?: string;
    customHasBorder?: boolean;
    customHoverBackgroundColor?: string;
    customHoverTextColor?: string;
    customHoverBorderColor?: string;
};

export const Button = ({
    children,
    className = '',
    target = '_self',
    type = 'primary',
    size = 'medium',
    variant = 'standard',
    fullWidth = false,
    disabled = false,
    relativePath = false,
    isSubmit = false,
    ariaDisabled = false,
    tabIndex,
    brandColor,
    customBackgroundColor,
    customTextColor,
    customBorderColor,
    customHasBorder = false,
    customHoverBackgroundColor,
    customHoverTextColor,
    customHoverBorderColor,
    ...props
}: PropsWithChildren<ButtonProps>) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { getStyle, useBrandedColorsBool } = useBrandedColors({ brandColor });

    const isCustomType = type === 'custom';
    const getCustomStyle = (): React.CSSProperties | undefined => {
        if (!isCustomType || !customBackgroundColor) {
            return undefined;
        }

        // Use provided hover colors or auto-generate by darkening
        const hoverBg = customHoverBackgroundColor || adjustHex(customBackgroundColor, 0.85);
        const hoverText = customHoverTextColor || customTextColor || '#ffffff';
        const hoverBorder = customHoverBorderColor || (customHasBorder ? adjustHex(customBorderColor || customBackgroundColor, 0.85) : 'transparent');

        return {
            '--custom-bg': customBackgroundColor,
            '--custom-bg-hover': hoverBg,
            '--custom-text': customTextColor || '#ffffff',
            '--custom-text-hover': hoverText,
            '--custom-border': customHasBorder ? customBorderColor || customBackgroundColor : 'transparent',
            '--custom-border-hover': hoverBorder,
        } as React.CSSProperties;
    };

    const classNames = clsx(
        'resurs-button r-mr-auto r-rounded-full r-transition-all r-gap-3 r-font-bold r-inline-flex r-items-center r-min-w-[90px] r-justify-center r-text-center',
        {
            'r-w-full': fullWidth,
            'r-min-w-[140px]': size !== 'tiny' && type !== 'link',
            'r-py-2 r-px-4': size === 'tiny',
            'r-text-sm r-px-5 r-py-2': size === 'small' && type !== 'link',
            'r-text-md r-px-8 r-py-3': size === 'medium' && type !== 'link',
            'r-text-xl r-px-12 r-py-5': size === 'large' && type !== 'link',
            'r-bg-[#ec9bba] hover:r-bg-[#f0afc6] r-text-black': type === 'pink',
            'r-bg-primary-200 r-text-white hover:r-bg-primary-300 focus-visible:r-bg-primary-300': type === 'primary',
            'r-text-primary-300 r-border r-border-primary-300 r-bg-white hover:r-bg-primary-100 focus-visible:r-bg-primary-100 focus-visible:r-text-white hover:r-border-primary-100 hover:r-text-white':
                type === 'secondary' || type === 'secondaryBorder' || type === 'tertiary',
            'r-text-primary r-bg-[#d6ebe5] hover:r-text-[#d6ebe5] hover:r-bg-primary focus-visible:r-text-[#d6ebe5] focus-visible:r-bg-primary':
                type === 'tertiaryNew',
            'r-text-[#f1eae8] r-border r-border-[#f1eae8] r-bg-transparent hover:r-text-[#2c2c2b] hover:r-border-[#e7dcd6] hover:r-bg-[#e7dcd6] focus-visible:r-text-[#2c2c2b] focus-visible:r-bg-[#e7dcd6]':
                type === 'transparent',
            'r-text-[#2c2c2b] r-border r-border-[#f1eae8] r-bg-[#f1eae8] hover:r-border-[#e7dcd6] hover:r-bg-[#e7dcd6] focus-visible:r-bg-[#e7dcd6]':
                type === 'sand',
            'custom-button': type === 'custom',
            'r-border': type === 'custom' && customHasBorder,
            'r-text-primary-300 r-px-0 r-underline r-underline-offset-2 r-font-base': type === 'link',
            'r-text-md': type !== 'link',
            'r-cursor-pointer': !disabled,
            'r-cursor-not-allowed r-opacity-75': disabled || ariaDisabled,
            'brand-styles': useBrandedColorsBool,
            primary: type === 'primary',
            secondary: type === 'secondary',
        },
        className
    );

    const anchor = () => {
        if (typeof props.to !== 'string') {
            return null;
        }

        const [, anchor] = props.to.split('#');
        return anchor;
    };

    const onClickHandler = () => {
        const documentAnchor = anchor();
        if (documentAnchor) {
            document.getElementById(documentAnchor)?.scrollIntoView({
                behavior: 'smooth',
            });

            if (props.to) {
                const [url] = props.to.split('#');
                const urlTo = removeTrailingSlash(url);

                // Only trigger a navigate callback if the url we should navigate to
                // is different to the current page.
                if (urlTo !== pathname) {
                    navigate(props.to);
                }
            }
        }

        if (props.onClick) {
            props.onClick();
        }
    };

    const renderContent = () => (
        <>
            {children}
            <Icon variant={variant} />
        </>
    );

    // This is a special case: If there is an anchor link - handle this as a button
    // and make sure we don't reload this page in the onClickHandler callback.
    // Otherwise the `to` property will trigger a rerender of the current page
    // which has some weird side-effects.
    if (typeof props.to === 'string' && !anchor()) {
        return (
            <Link
                to={props.to}
                target={target}
                data-component="button"
                className={classNames}
                onClick={onClickHandler}
                relative={relativePath ? 'path' : 'route'}
                {...(tabIndex !== undefined ? { tabIndex } : {})}
                style={useBrandedColorsBool ? getStyle() : isCustomType ? getCustomStyle() : {}}
            >
                {renderContent()}
            </Link>
        );
    }

    return (
        <button
            data-component="button"
            className={classNames}
            onClick={onClickHandler}
            disabled={disabled}
            aria-disabled={ariaDisabled}
            type={isSubmit ? 'submit' : 'button'}
            {...(tabIndex !== undefined ? { tabIndex } : {})}
            style={useBrandedColorsBool ? getStyle() : isCustomType ? getCustomStyle() : {}}
        >
            {renderContent()}
        </button>
    );
};
